import { supabase } from '@/lib/supabase';
import { TIME_OFF_TYPES as _TYPES } from '@/lib/types';
export const TIME_OFF_TYPES = _TYPES;
import type {
  HalfDayPart,
  TimeOffConflictShift,
  TimeOffRequest,
  TimeOffStatus,
  TimeOffType,
} from '@/lib/types';

/**
 * Time-off API helpers (Day 7).
 *
 * All mutations go through SECURITY DEFINER RPCs:
 *   time_off_request_create / time_off_request_withdraw
 *   time_off_transition (approve | deny)
 *   time_off_cancel
 *
 * Reads go directly against the time_off_requests table; RLS scopes them
 * via app_user_visible_time_off_ids().
 *
 * The conflict view is fetched on-demand for the approver UI via
 * time_off_conflicting_shifts(request_id).
 */

// ----- reads ----------------------------------------------------------------

export async function listTimeOffRequests(): Promise<TimeOffRequest[]> {
  const { data, error } = await supabase
    .from('time_off_requests')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as TimeOffRequest[];
}

export async function listTimeOffForStaff(
  staffId: string
): Promise<TimeOffRequest[]> {
  const { data, error } = await supabase
    .from('time_off_requests')
    .select('*')
    .eq('staff_id', staffId)
    .order('start_date', { ascending: false });
  if (error) throw error;
  return (data ?? []) as TimeOffRequest[];
}

export async function getTimeOffRequest(
  id: string
): Promise<TimeOffRequest | null> {
  const { data, error } = await supabase
    .from('time_off_requests')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as TimeOffRequest | null;
}

export async function listConflictingShifts(
  requestId: string
): Promise<TimeOffConflictShift[]> {
  const { data, error } = await supabase.rpc('time_off_conflicting_shifts', {
    p_request_id: requestId,
  });
  if (error) throw error;
  return (data ?? []) as TimeOffConflictShift[];
}

// ----- writes (RPCs) --------------------------------------------------------

export interface TimeOffCreatePayload {
  staff_id: string;
  type: TimeOffType;
  start_date: string;
  end_date: string;
  starts_half_day?: HalfDayPart | null;
  ends_half_day?: HalfDayPart | null;
  reason?: string | null;
}

export async function createTimeOffRequest(
  payload: TimeOffCreatePayload
): Promise<TimeOffRequest> {
  const { data, error } = await supabase.rpc('time_off_request_create', {
    p_staff_id: payload.staff_id,
    p_type: payload.type,
    p_start_date: payload.start_date,
    p_end_date: payload.end_date,
    p_starts_half_day: payload.starts_half_day ?? null,
    p_ends_half_day: payload.ends_half_day ?? null,
    p_reason: payload.reason ?? null,
  });
  if (error) throw error;
  return data as TimeOffRequest;
}

export async function withdrawTimeOffRequest(
  id: string,
  reason: string | null = null
): Promise<TimeOffRequest> {
  const { data, error } = await supabase.rpc('time_off_request_withdraw', {
    p_request_id: id,
    p_reason: reason,
  });
  if (error) throw error;
  return data as TimeOffRequest;
}

export async function transitionTimeOff(
  id: string,
  toStatus: Extract<TimeOffStatus, 'approved' | 'denied'>,
  note: string | null = null
): Promise<TimeOffRequest> {
  const { data, error } = await supabase.rpc('time_off_transition', {
    p_request_id: id,
    p_to_status: toStatus,
    p_note: note,
  });
  if (error) throw error;
  return data as TimeOffRequest;
}

export async function cancelTimeOff(
  id: string,
  reason: string | null = null
): Promise<TimeOffRequest> {
  const { data, error } = await supabase.rpc('time_off_cancel', {
    p_request_id: id,
    p_reason: reason,
  });
  if (error) throw error;
  return data as TimeOffRequest;
}

// ----- formatting helpers ---------------------------------------------------

/** Render the request's date range, accounting for half-day markers. */
export function formatTimeOffRange(req: TimeOffRequest): string {
  const sameDay = req.start_date === req.end_date;
  const fmt = (iso: string) => {
    // "2026-04-26" -> "Apr 26, 2026"
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (sameDay) {
    if (req.starts_half_day) {
      return `${fmt(req.start_date)} (${req.starts_half_day} only)`;
    }
    return fmt(req.start_date);
  }

  const startSuffix = req.starts_half_day ? ` (${req.starts_half_day})` : '';
  const endSuffix = req.ends_half_day ? ` (${req.ends_half_day})` : '';
  return `${fmt(req.start_date)}${startSuffix} – ${fmt(req.end_date)}${endSuffix}`;
}

/** Approximate days requested, honoring half-day markers as 0.5. */
export function timeOffDayCount(req: TimeOffRequest): number {
  const [sy, sm, sd] = req.start_date.split('-').map(Number);
  const [ey, em, ed] = req.end_date.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const ms = end.getTime() - start.getTime();
  let days = Math.round(ms / (1000 * 60 * 60 * 24)) + 1;
  if (req.start_date === req.end_date) {
    return req.starts_half_day ? 0.5 : 1;
  }
  if (req.starts_half_day) days -= 0.5;
  if (req.ends_half_day) days -= 0.5;
  return days;
}

export const TIME_OFF_STATUS_LABELS: Record<TimeOffStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  denied: 'Denied',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
};
